import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Buildings, UsersFour, Barbell, ShieldCheck, Envelope, Copy, Trash,
  Plus, Gear, Globe, Trophy, ChartLineUp, ClipboardText, Calendar, CurrencyInr,
  Megaphone, ThumbsUp, ThumbsDown, Question, Link as LinkIcon, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAcademy } from "@/contexts/AcademyContext";
import { AcademyPlanBilling } from "./AcademyPlanBilling";
import { AcademyOnboarding } from "./AcademyOnboarding";
import { AcademyGroupTests } from "./AcademyGroupTests";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  starter:    { label: "STARTER",    color: "text-text-secondary" },
  growth:     { label: "GROWTH",     color: "text-lime-300" },
  pro:        { label: "PRO",        color: "text-brand-accent" },
  enterprise: { label: "ENTERPRISE", color: "text-heat" },
  elite:      { label: "ELITE",      color: "text-heat" },
};

// Mirrors backend ACADEMY_PLAN_LIMITS (null = unlimited). Display fallback when
// the API payload predates seat limits.
const PLAN_LIMITS_FALLBACK = {
  starter:    { coaches: 2,    athletes: 30 },
  growth:     { coaches: 5,    athletes: 100 },
  pro:        { coaches: 15,   athletes: 300 },
  enterprise: { coaches: null, athletes: null },
  elite:      { coaches: null, athletes: null },
};

const TABS = [
  { id: "overview",      label: "Overview" },
  { id: "analytics",     label: "Analytics" },
  { id: "coaches",       label: "Coaches" },
  { id: "athletes",      label: "Athletes" },
  { id: "tests",         label: "Group Tests" },
  { id: "attendance",    label: "Attendance" },
  { id: "events",        label: "Events" },
  { id: "fees",          label: "Fees" },
  { id: "announcements", label: "Announcements" },
  { id: "invites",       label: "Invites" },
  { id: "settings",      label: "Settings" },
];

// R5 — per-role chip colors on the Invites tab
const ROLE_CHIP = {
  strength_coach:  "bg-heat/15 text-heat",
  coach:           "bg-heat/15 text-heat",           // legacy alias
  fitness_trainer: "bg-lime-400/15 text-lime-300",
  sports_coach:    "bg-cyan-400/15 text-cyan-300",
  athlete:         "bg-brand-accent/15 text-brand-accent",
  general_client:  "bg-white/15 text-white",
  parent:          "bg-amber-400/15 text-amber-300",
};

// R6 — feature flags an Academy Admin can toggle per member.
// Keys MUST match backend `TOGGLEABLE_FEATURES`.
const FEATURE_FLAGS = [
  { key: "advanced_sc",   label: "Advanced S&C",       desc: "Program Builder, Test Library, Performance Testing" },
  { key: "nutrition",     label: "Nutrition",          desc: "Meals & macros logging + planning" },
  { key: "messaging",     label: "Messaging",          desc: "Coach ↔ athlete direct messages" },
  { key: "reports",       label: "Reports",            desc: "Analytics + PDF reports" },
  { key: "competitions",  label: "Competitions",       desc: "Competition planning module" },
  { key: "sport_science", label: "Sport Science",      desc: "Velocity, training load, sport science" },
];

const avatar = (m, size = 9) => {
  if (m.picture) {
    const src = m.picture.startsWith("/api/") ? `${BACKEND_URL}${m.picture}` : m.picture;
    return <img src={src} alt="" className={`w-${size} h-${size} rounded-full object-cover`} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-brand-accent/15 flex items-center justify-center text-xs font-bold text-brand-accent`}>
      {(m.name || "?")[0].toUpperCase()}
    </div>
  );
};

const SeatBar = ({ label, used, cap, testid }) => {
  const unlimited = cap == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, cap)) * 100));
  const full = !unlimited && used >= cap;
  const near = !unlimited && !full && used / cap >= 0.8;
  const barColor = full ? "bg-heat" : near ? "bg-amber-400" : "bg-brand-accent";
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="label-tiny text-[11px] text-text-secondary">{label}</span>
        <span className={`label-tiny text-[11px] ${full ? "text-heat" : "text-white"}`}>
          {used} / {unlimited ? "∞" : cap}
        </span>
      </div>
      <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: unlimited ? "8%" : `${pct}%` }} />
      </div>
    </div>
  );
};

const SeatUsagePanel = ({ plan, limits, usage, onUpgrade }) => {
  const coachFull = limits.coaches != null && (usage.coaches ?? 0) >= limits.coaches;
  const athFull = limits.athletes != null && (usage.athletes ?? 0) >= limits.athletes;
  const anyFull = coachFull || athFull;
  const planLabel = PLAN_META[plan]?.label || "STARTER";
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="academy-seat-usage">
      <div className="flex items-center justify-between gap-2">
        <span className="label-tiny text-[11px] text-text-secondary">PLAN USAGE · {planLabel}</span>
        {onUpgrade && (
          <button
            onClick={onUpgrade}
            className="label-tiny text-[11px] text-brand-accent hover:underline inline-flex items-center gap-1"
            data-testid="academy-seat-upgrade-link"
          >
            <Trophy size={12} weight="fill" /> UPGRADE
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SeatBar label="COACHES" used={usage.coaches ?? 0} cap={limits.coaches} testid="academy-seat-coaches" />
        <SeatBar label="ATHLETES" used={usage.athletes ?? 0} cap={limits.athletes} testid="academy-seat-athletes" />
      </div>
      {anyFull && (
        <div className="flex items-start gap-2 border border-amber-400/40 bg-[#241a07] px-3 py-2 text-[11px] text-amber-50" data-testid="academy-seat-limit-banner">
          <Trophy size={13} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
          <span>
            You've hit your {planLabel} plan limit for {coachFull && athFull ? "coaches and athletes" : coachFull ? "coaches" : "athletes"}.{" "}
            {onUpgrade && (
              <button onClick={onUpgrade} className="underline text-amber-200" data-testid="academy-seat-limit-banner-upgrade">
                Upgrade your plan
              </button>
            )}{" "}to add more.
          </span>
        </div>
      )}
    </div>
  );
};

export default function Academy() {
  const { academy, role, loading, reload } = useAcademy();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : "overview";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: false });
  const renewTier = searchParams.get("renew") || null;
  const renewCycle = searchParams.get("cycle") || "monthly";
  const clearRenewParams = useCallback(() => {
    setSearchParams({ tab: "settings" }, { replace: true });
  }, [setSearchParams]);
  const [coaches, setCoaches] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [invites, setInvites] = useState([]);
  const isAdmin = role === "admin";
  const isCoachRole = ["strength_coach", "fitness_trainer", "sports_coach", "coach"].includes(role);
  const [inviteRole, setInviteRole] = useState("athlete");
  const [inviteExpires, setInviteExpires] = useState(14);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editWeb, setEditWeb] = useState("");
  const [editPlan, setEditPlan] = useState("starter");
  const [editSport, setEditSport] = useState("");
  const [editColor, setEditColor] = useState("");
  const [sportOptions, setSportOptions] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // R6 — permissions editor state
  const [permTarget, setPermTarget] = useState(null);
  const [permDraft, setPermDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  const loadLists = useCallback(() => {
    if (!academy) return;
    // R5/R6: fetch all members then group. Coach-like roles include the
    // legacy `coach` value + new canonical roles introduced in R1/R5.
    api.get(`/academies/${academy.academy_id}/members`).then(({ data }) => {
      const coachRoles = new Set(["coach", "strength_coach", "fitness_trainer", "sports_coach"]);
      setCoaches(data.filter((m) => coachRoles.has(m.role)));
      setAthletes(data.filter((m) => !coachRoles.has(m.role) && m.role !== "admin"));
    }).catch(() => {});
    if (isAdmin) {
      api.get(`/academies/${academy.academy_id}/invites`).then(({ data }) => setInvites(data)).catch(() => {});
    }
  }, [academy, isAdmin]);

  useEffect(() => {
    if (!loading && !academy) {
      // Not in any academy → academies are provisioned by the platform admin.
      return;
    }
    if (academy) {
      setEditName(academy.name || "");
      setEditDesc(academy.description || "");
      setEditWeb(academy.website || "");
      setEditPlan(academy.plan || "starter");
      setEditSport(academy.sport || "");
      setEditColor(academy.brand_color || "");
      loadLists();
    }
  }, [academy, loading, nav, loadLists]);

  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!academy || role !== "admin") return;
    if (sportOptions.length === 0) {
      api.get(`/academies/${academy.academy_id}/assessment-templates`)
        .then(({ data }) => setSportOptions(data.sports || []))
        .catch(() => {});
    }
  }, [academy, role, sportOptions.length]);
  useEffect(() => {
    if (!academy || role !== "admin" || autoOpenedRef.current) return;
    const os = academy.onboarding_state;
    if (os && !os.completed && !os.dismissed) {
      autoOpenedRef.current = true;
      setOnboardingOpen(true);
    }
  }, [academy, role]);

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      await api.post(`/academies/${academy.academy_id}/invites`, {
        role: inviteRole,
        expires_in_days: Number(inviteExpires) || 14,
      });
      toast.success("Invite created");
      setInviteOpen(false);
      loadLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invite");
    } finally {
      setCreatingInvite(false);
    }
  };

  const revokeInvite = async (code) => {
    if (!window.confirm(`Revoke invite ${code}?`)) return;
    try {
      await api.delete(`/academies/${academy.academy_id}/invites/${code}`);
      toast.success("Revoked");
      loadLists();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const copyInvite = (code) => {
    const link = `${window.location.origin}/academy-join/${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from academy?`)) return;
    try {
      await api.delete(`/academies/${academy.academy_id}/members/${uid}`);
      toast.success("Removed");
      loadLists();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  // R6 — permission overrides
  const openPermissions = (m) => {
    setPermTarget(m);
    setPermDraft(m.permissions || {});
  };
  const togglePerm = (flag) => setPermDraft((prev) => ({ ...prev, [flag]: !prev[flag] }));
  const savePermissions = async () => {
    if (!permTarget) return;
    setSavingPerms(true);
    try {
      const { data } = await api.patch(
        `/academies/${academy.academy_id}/members/${permTarget.user_id}/permissions`,
        { permissions: permDraft },
      );
      toast.success(`Permissions updated for ${permTarget.name}`);
      // Update local list without full refetch
      setCoaches((prev) => prev.map((m) => m.user_id === permTarget.user_id ? { ...m, permissions: data.permissions } : m));
      setAthletes((prev) => prev.map((m) => m.user_id === permTarget.user_id ? { ...m, permissions: data.permissions } : m));
      setPermTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch(`/academies/${academy.academy_id}`, {
        name: editName,
        description: editDesc,
        website: editWeb,
        sport: editSport || "",
        brand_color: editColor || "",
      });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/academies/${academy.academy_id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    }
  };

  if (loading) return <div className="p-10 label-tiny text-text-secondary" data-testid="academy-loading">Loading academy...</div>;
  if (!academy) return (
    <div className="max-w-xl mx-auto p-6 lg:p-10" data-testid="academy-none">
      <div className="tactical-card p-8 text-center">
        <Buildings size={32} weight="duotone" className="text-brand-accent mx-auto mb-3" />
        <h1 className="font-heading text-2xl mb-2">NO ACADEMY YET</h1>
        <p className="text-sm text-text-secondary">
          You&apos;re not part of an academy. Academies are set up by the Edge Training team, who
          then invite an admin. If you&apos;re expecting an invitation, check your email for your
          join link, or{" "}
          <a href="mailto:admin@edgetraining.com" className="text-brand-accent underline">contact us</a>.
        </p>
      </div>
    </div>
  );

  const counts = academy.counts || { admin: 0, coach: 0, athlete: 0, total: 0 };
  const plan = PLAN_META[academy.plan] || PLAN_META.starter;
  const limits = academy.plan_limits || PLAN_LIMITS_FALLBACK[academy.plan] || PLAN_LIMITS_FALLBACK.starter;
  const usage = academy.seat_usage || { coaches: counts.coach, athletes: counts.athlete };
  const roleBucket = (r) =>
    ["strength_coach", "fitness_trainer", "sports_coach", "coach"].includes(r)
      ? "coaches"
      : ["athlete", "general_client"].includes(r)
        ? "athletes"
        : null;
  const bucketFull = (b) => !!b && limits[b] != null && (usage[b] ?? 0) >= limits[b];
  const selBucket = roleBucket(inviteRole);
  const selFull = bucketFull(selBucket);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="academy-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent tracking-widest mb-2">// {isAdmin ? "ADMIN" : role?.toUpperCase()} · ACADEMY</div>
          <h1 className="font-heading text-4xl sm:text-5xl" data-testid="academy-name">{academy.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <span className={`label-tiny text-[11px] ${plan.color}`} data-testid="academy-plan-badge">
              PLAN · {plan.label}
            </span>
            <Link
              to={`/a/${academy.slug}`}
              target="_blank"
              className="label-tiny text-[11px] text-text-secondary hover:text-brand-accent inline-flex items-center gap-1"
              data-testid="academy-public-link"
            >
              <Globe size={11} /> /a/{academy.slug}
            </Link>
          </div>
        </div>
        {isAdmin && (
          <Link
            to="/coach"
            className="inline-flex items-center gap-2 border border-brand-accent text-brand-accent px-4 py-2.5 text-sm label-tiny tracking-wider hover:bg-brand-accent/10 transition"
            data-testid="academy-switch-to-coach"
          >
            SWITCH TO COACH VIEW →
          </Link>
        )}
      </div>

      {/* Onboarding setup banner + wizard (admin only) */}
      {isAdmin && academy.onboarding_state && !academy.onboarding_state.completed && (
        <div
          className="border border-brand-accent/40 bg-brand-accent/10 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="academy-onboarding-banner"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={16} weight="fill" className="text-brand-accent" />
              <span className="font-heading text-sm">Finish setting up your academy</span>
            </div>
            <div className="h-1.5 w-full max-w-md bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-accent transition-all duration-500"
                style={{ width: `${academy.onboarding_state.percent}%` }}
                data-testid="academy-onboarding-banner-progress"
              />
            </div>
            <span className="text-[11px] label-tiny text-text-secondary mt-1 inline-block">
              {academy.onboarding_state.done_count} of {academy.onboarding_state.total} steps complete
            </span>
          </div>
          <button
            onClick={() => setOnboardingOpen(true)}
            className="bg-brand-accent text-surface-base px-5 py-2.5 text-xs label-tiny tracking-wider inline-flex items-center gap-2 shrink-0"
            data-testid="academy-onboarding-open"
          >
            CONTINUE SETUP →
          </button>
        </div>
      )}
      {isAdmin && (
        <AcademyOnboarding
          academyId={academy.academy_id}
          academy={academy}
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
          onChanged={reload}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-white/10 flex gap-1 overflow-x-auto" data-testid="academy-tabs">
        {TABS.filter((t) => {
          if (!isAdmin) {
            // Members see limited tabs (no invites/settings/attendance-marking/fees-mgmt)
            return ["overview", "events", "announcements"].includes(t.id) ||
                   (t.id === "coaches" || t.id === "athletes") ||
                   (t.id === "tests" && isCoachRole);
          }
          return true;
        }).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`academy-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm label-tiny tracking-wider transition border-b-2 -mb-px ${
              tab === t.id
                ? "border-brand-accent text-brand-accent"
                : "border-transparent text-text-secondary hover:text-brand-accent"
            }`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="academy-overview">
          <StatCard icon={<Buildings size={20} weight="duotone" />} label="TOTAL MEMBERS" value={counts.total} />
          <StatCard icon={<ShieldCheck size={20} weight="duotone" />} label="ADMINS" value={counts.admin} />
          <StatCard icon={<UsersFour size={20} weight="duotone" />} label="COACHES" value={counts.coach} />
          <StatCard icon={<Barbell size={20} weight="duotone" />} label="ATHLETES" value={counts.athlete} />
          {academy.description && (
            <div className="col-span-2 md:col-span-4 border border-white/10 p-5 bg-white/[0.02]">
              <div className="label-tiny text-text-secondary mb-1">ABOUT</div>
              <div className="text-sm">{academy.description}</div>
            </div>
          )}
        </div>
      )}

      {/* Coaches */}
      {tab === "coaches" && (
        <MemberList
          members={coaches}
          emptyIcon={<UsersFour size={40} className="mx-auto text-brand-accent mb-2" weight="duotone" />}
          emptyText="No coaches yet. Create a coach invite from the Invites tab."
          isAdmin={isAdmin}
          onRemove={removeMember}
          onPermissions={openPermissions}
          testidPrefix="academy-coach"
        />
      )}

      {/* Athletes */}
      {tab === "athletes" && (
        <MemberList
          members={athletes}
          emptyIcon={<Barbell size={40} className="mx-auto text-brand-accent mb-2" weight="duotone" />}
          emptyText="No athletes yet. Share an athlete invite link."
          isAdmin={isAdmin}
          onRemove={removeMember}
          onPermissions={openPermissions}
          testidPrefix="academy-athlete"
        />
      )}

      {/* Group Tests */}
      {tab === "tests" && (isAdmin || isCoachRole) && (
        <AcademyGroupTests academyId={academy.academy_id} academy={academy} athletes={athletes} coaches={coaches} canManage={isAdmin} />
      )}

      {/* R6 — Permissions editor dialog */}
      <Dialog open={!!permTarget} onOpenChange={(o) => !o && setPermTarget(null)}>
        <DialogContent data-testid="academy-permissions-dialog">
          <DialogHeader>
            <DialogTitle>Permissions — {permTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-text-secondary mb-3">
            Grant this user access to advanced modules regardless of their role. Useful for temporarily upgrading a Fitness Trainer or Sports Coach.
          </div>
          <div className="space-y-2">
            {FEATURE_FLAGS.map((f) => (
              <label key={f.key} className="flex items-center justify-between border border-white/10 px-3 py-2 cursor-pointer hover:bg-white/[0.03]" data-testid={`perm-row-${f.key}`}>
                <div>
                  <div className="text-sm">{f.label}</div>
                  <div className="text-[10px] text-text-secondary">{f.desc}</div>
                </div>
                <input
                  type="checkbox"
                  checked={!!permDraft?.[f.key]}
                  onChange={() => togglePerm(f.key)}
                  className="w-4 h-4"
                  data-testid={`perm-toggle-${f.key}`}
                />
              </label>
            ))}
          </div>
          <DialogFooter>
            <button
              onClick={savePermissions}
              disabled={savingPerms}
              data-testid="perm-save-btn"
              className="bg-brand-accent text-surface-base px-4 py-2 label-tiny text-xs disabled:opacity-50"
            >
              {savingPerms ? "SAVING..." : "SAVE PERMISSIONS"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invites */}
      {tab === "invites" && isAdmin && (
        <div className="space-y-4" data-testid="academy-invites">
          <SeatUsagePanel plan={academy.plan} limits={limits} usage={usage} onUpgrade={() => setTab("settings")} />
          <div className="flex justify-end">
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-4 py-2.5 text-sm label-tiny tracking-wider hover:brightness-110 transition"
                  data-testid="academy-invite-create-btn"
                >
                  <Plus size={16} weight="bold" /> NEW INVITE
                </button>
              </DialogTrigger>
              <DialogContent data-testid="academy-invite-dialog">
                <DialogHeader><DialogTitle>Create invite link</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <label className="text-xs label-tiny text-text-secondary">ROLE</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm"
                    data-testid="academy-invite-role-select"
                  >
                    <option value="athlete">Athlete</option>
                    <option value="strength_coach">Strength Coach</option>
                    <option value="fitness_trainer">Fitness Trainer</option>
                    <option value="sports_coach">Sports Coach</option>
                    <option value="general_client">General Client</option>
                    <option value="parent">Parent / Guardian</option>
                  </select>
                  <label className="text-xs label-tiny text-text-secondary">EXPIRES IN (DAYS)</label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={inviteExpires}
                    onChange={(e) => setInviteExpires(e.target.value)}
                    data-testid="academy-invite-expires-input"
                  />
                  {selFull && (
                    <div className="flex items-start gap-2 border border-amber-400/40 bg-[#241a07] px-3 py-2 text-[11px] text-amber-50" data-testid="academy-invite-limit-warning">
                      <Trophy size={13} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
                      <span>
                        You've reached your {PLAN_META[academy.plan]?.label || "STARTER"} limit of{" "}
                        {limits[selBucket]} {selBucket}. Upgrade your plan in{" "}
                        <button type="button" onClick={() => { setInviteOpen(false); setTab("settings"); }} className="underline text-amber-200" data-testid="academy-invite-limit-upgrade">
                          Settings
                        </button>{" "}
                        to invite more.
                      </span>
                    </div>
                  )}
                  <button
                    onClick={createInvite}
                    disabled={creatingInvite || selFull}
                    className="w-full bg-brand-accent text-surface-base py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="academy-invite-submit"
                  >
                    {selFull ? "LIMIT REACHED — UPGRADE TO INVITE" : creatingInvite ? "CREATING..." : "CREATE INVITE"}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {invites.length === 0 ? (
            <EmptyBlock
              icon={<Envelope size={40} className="mx-auto text-brand-accent mb-2" weight="duotone" />}
              text="No active invites. Create one to onboard coaches or athletes."
              testid="academy-invites-empty"
            />
          ) : (
            <div className="border border-white/10 divide-y divide-white/5">
              {invites.map((i) => (
                <div key={i.invite_id} className="flex items-center gap-4 p-4" data-testid={`academy-invite-row-${i.code}`}>
                  <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 ${
                    ROLE_CHIP[i.role] || "bg-white/10 text-text-secondary"
                  }`}>
                    {i.role.replace(/_/g, " ").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm">{i.code}</div>
                    <div className="text-[11px] text-text-secondary">
                      Uses: {i.uses}{i.max_uses ? ` / ${i.max_uses}` : ""} · Expires {new Date(i.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => copyInvite(i.code)}
                    className="text-text-secondary hover:text-brand-accent p-2"
                    data-testid={`academy-invite-copy-${i.code}`}
                    aria-label="Copy invite link"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => revokeInvite(i.code)}
                    className="text-text-secondary hover:text-red-500 p-2"
                    data-testid={`academy-invite-revoke-${i.code}`}
                    aria-label="Revoke invite"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      {tab === "settings" && isAdmin && (
        <div className="border border-white/10 bg-white/[0.02] p-5 space-y-4 max-w-4xl" data-testid="academy-settings">
          <div className="flex items-center gap-2">
            <Gear size={18} className="text-brand-accent" weight="duotone" />
            <div className="font-heading text-lg">Academy settings</div>
          </div>
          <div>
            <label className="text-xs label-tiny text-text-secondary">NAME</label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="academy-settings-name" />
          </div>
          <div>
            <label className="text-xs label-tiny text-text-secondary">DESCRIPTION</label>
            <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={3} data-testid="academy-settings-desc" />
          </div>
          <div>
            <label className="text-xs label-tiny text-text-secondary">WEBSITE</label>
            <Input value={editWeb} onChange={(e) => setEditWeb(e.target.value)} placeholder="https://..." data-testid="academy-settings-web" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs label-tiny text-text-secondary">PRIMARY SPORT</label>
              <select
                value={editSport}
                onChange={(e) => setEditSport(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2.5 text-sm"
                data-testid="academy-settings-sport"
              >
                <option value="">— Select —</option>
                {sportOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs label-tiny text-text-secondary">BRAND COLOUR</label>
              <div className="flex items-center gap-2 mt-0.5">
                <input
                  type="color"
                  value={editColor || "#FF5A1F"}
                  onChange={(e) => setEditColor(e.target.value)}
                  className="w-10 h-10 bg-transparent border border-white/10 rounded cursor-pointer"
                  data-testid="academy-settings-color"
                />
                <span className="text-xs text-text-secondary font-mono">{(editColor || "").toUpperCase() || "—"}</span>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs label-tiny text-text-secondary mb-2 block">LOGO</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                {academy.logo_url
                  ? <img src={academy.logo_url.startsWith("/api/") ? `${BACKEND_URL}${academy.logo_url}` : academy.logo_url} alt="logo" className="w-full h-full object-contain" />
                  : <Buildings size={24} className="text-white/30" />}
              </div>
              <label className="border border-brand-accent text-brand-accent px-4 py-2 text-xs label-tiny tracking-wider inline-flex items-center gap-2 hover:bg-brand-accent/10 cursor-pointer" data-testid="academy-settings-logo-upload">
                <input type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="academy-settings-logo-file" />
                {academy.logo_url ? "REPLACE LOGO" : "UPLOAD LOGO"}
              </label>
            </div>
          </div>
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="bg-brand-accent text-surface-base px-5 py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60"
            data-testid="academy-settings-save"
          >
            {savingSettings ? "SAVING..." : "SAVE CHANGES"}
          </button>
          <div className="pt-5 mt-1 border-t border-white/10 space-y-4">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-brand-accent" weight="fill" />
              <div className="font-heading text-lg">Plan &amp; billing</div>
            </div>
            <AcademyPlanBilling
              academyId={academy.academy_id}
              onChanged={reload}
              autoRenewTier={renewTier}
              autoRenewCycle={renewCycle}
              onAutoRenewHandled={clearRenewParams}
            />
          </div>
        </div>
      )}

      {/* Analytics */}
      {tab === "analytics" && isAdmin && <AnalyticsTab academyId={academy.academy_id} />}

      {/* Attendance */}
      {tab === "attendance" && isAdmin && <AttendanceTab academyId={academy.academy_id} athletes={athletes} />}

      {/* Events */}
      {tab === "events" && <EventsTab academyId={academy.academy_id} isAdmin={isAdmin} />}

      {/* Fees */}
      {tab === "fees" && isAdmin && <FeesTab academyId={academy.academy_id} athletes={athletes} />}

      {/* Announcements */}
      {tab === "announcements" && <AnnouncementsTab academyId={academy.academy_id} isAdmin={isAdmin} />}

      {/* Bulk invite fold-out inside Invites tab */}
      {tab === "invites" && isAdmin && <BulkInvitePanel academyId={academy.academy_id} onCreated={loadLists} />}
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5">
    <div className="text-brand-accent mb-2">{icon}</div>
    <div className="text-3xl font-heading tabular-nums" data-testid={`academy-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</div>
    <div className="label-tiny text-[10px] text-text-secondary mt-1">{label}</div>
  </div>
);

const EmptyBlock = ({ icon, text, testid }) => (
  <div className="border border-white/10 p-10 text-center" data-testid={testid}>
    {icon}
    <div className="text-sm text-text-secondary">{text}</div>
  </div>
);

const MemberList = ({ members, emptyIcon, emptyText, isAdmin, onRemove, onPermissions, testidPrefix }) => {
  if (members.length === 0) {
    return <EmptyBlock icon={emptyIcon} text={emptyText} testid={`${testidPrefix}s-empty`} />;
  }
  return (
    <div className="border border-white/10 divide-y divide-white/5" data-testid={`${testidPrefix}s-list`}>
      {members.map((m) => (
        <div key={m.user_id} className="flex items-center gap-4 p-4" data-testid={`${testidPrefix}-row-${m.user_id}`}>
          {avatar(m)}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{m.name}</div>
            <div className="text-[11px] text-text-secondary truncate">{m.email}</div>
            {(m.primary_role || m.role) && (
              <div className={`inline-block mt-1 label-tiny text-[9px] tracking-widest px-1.5 py-0.5 ${
                ROLE_CHIP[m.primary_role] || ROLE_CHIP[m.role] || "bg-white/10 text-text-secondary"
              }`}>
                {(m.primary_role || m.role).replace(/_/g, " ").toUpperCase()}
              </div>
            )}
          </div>
          <div className="label-tiny text-[10px] text-text-secondary hidden md:block">
            {new Date(m.added_at).toLocaleDateString()}
          </div>
          {isAdmin && onPermissions && (
            <button
              onClick={() => onPermissions(m)}
              className="text-text-secondary hover:text-brand-accent px-2 label-tiny text-[10px]"
              data-testid={`${testidPrefix}-permissions-${m.user_id}`}
            >
              PERMISSIONS
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => onRemove(m.user_id, m.name)}
              className="text-text-secondary hover:text-red-500 p-2"
              data-testid={`${testidPrefix}-remove-${m.user_id}`}
              aria-label={`Remove ${m.name}`}
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};


// ─── ACADEMY ADMIN COMMAND DECK — NEW TABS ─────────────────────────────────

const Card = ({ label, value, sublabel, accent }) => (
  <div className="border border-white/10 bg-white/[0.02] p-4">
    <div className={`text-2xl font-heading tabular-nums ${accent === "heat" ? "text-heat" : "text-brand-accent"}`}>{value}</div>
    <div className="label-tiny text-[10px] text-text-secondary mt-1">{label}</div>
    {sublabel && <div className="text-[11px] text-text-secondary mt-0.5">{sublabel}</div>}
  </div>
);

const AnalyticsTab = ({ academyId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get(`/academies/${academyId}/analytics`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setBusy(false));
  }, [academyId]);
  if (busy) return <div className="label-tiny text-text-secondary">Loading analytics...</div>;
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="academy-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="TOTAL MEMBERS" value={data.counts.total} />
        <Card label="ATHLETES" value={data.counts.athlete} />
        <Card label="COACHES" value={data.counts.coach} />
        <Card label="NEW · LAST 30D" value={data.growth.last_30d} accent="heat" />
      </div>
      <div>
        <div className="label-tiny text-[10px] text-brand-accent mb-2">// ENGAGEMENT</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card label="ACTIVE · 7D" value={data.engagement.active_athletes_7d} />
          <Card label="ACTIVE · 30D" value={data.engagement.active_athletes_30d} />
          <Card label="SILENT · 14D" value={data.engagement.silent_14d} accent="heat" />
          <Card label="WORKOUTS · 7D" value={data.engagement.workouts_7d} />
          <Card label="PRS · 7D" value={data.engagement.prs_7d} />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10">
          <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// COACH LOAD</div>
          {data.coach_load.length === 0 && <div className="p-6 text-center text-text-secondary text-sm">No coaches yet.</div>}
          {data.coach_load.map((c) => (
            <div key={c.user_id} className="p-3 flex justify-between border-t border-white/5" data-testid={`analytics-coach-load-${c.user_id}`}>
              <span className="text-sm">{c.name}</span>
              <span className="text-sm font-heading text-brand-accent tabular-nums">{c.client_count} clients</span>
            </div>
          ))}
        </div>
        <div className="border border-white/10">
          <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// TOP ATHLETES · 30D WORKOUTS</div>
          {data.top_athletes.length === 0 && <div className="p-6 text-center text-text-secondary text-sm">No workout data yet.</div>}
          {data.top_athletes.map((a, i) => (
            <div key={a.user_id} className="p-3 flex items-center gap-3 border-t border-white/5">
              <span className="w-5 text-text-secondary font-mono">{i + 1}</span>
              <span className="flex-1 text-sm">{a.name}</span>
              <span className="text-sm font-heading text-heat tabular-nums">{a.workouts_30d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const STATUS_OPTIONS = [
  { key: "present", label: "PRESENT", color: "bg-green-500/15 text-green-500" },
  { key: "absent",  label: "ABSENT",  color: "bg-red-500/15 text-red-500" },
  { key: "late",    label: "LATE",    color: "bg-yellow-500/15 text-yellow-500" },
  { key: "excused", label: "EXCUSED", color: "bg-white/10 text-text-secondary" },
];

const AttendanceTab = ({ academyId, athletes }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [session, setSession] = useState("Morning Practice");
  const [marks, setMarks] = useState({});
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/academies/${academyId}/attendance?date=${date}`);
      const map = {};
      (data.items || []).forEach((r) => { map[r.athlete_id] = r.status; });
      setMarks(map);
      setRows(data.items || []);
      const { data: s } = await api.get(`/academies/${academyId}/attendance/summary?days=30`);
      setSummary(s.items || []);
    } finally { setBusy(false); }
  }, [academyId, date]);
  useEffect(() => { load(); }, [load]);

  const mark = (aid, status) => setMarks((m) => ({ ...m, [aid]: status }));

  const save = async () => {
    const payload = Object.entries(marks).map(([athlete_id, status]) => ({ athlete_id, status }));
    if (payload.length === 0) { toast.error("Mark at least one athlete"); return; }
    try {
      await api.post(`/academies/${academyId}/attendance`, { date, session_label: session, marks: payload });
      toast.success(`Saved ${payload.length} attendance record(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4" data-testid="academy-attendance">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary">DATE</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date" />
          </div>
          <div className="md:col-span-2">
            <label className="label-tiny text-[10px] text-text-secondary">SESSION</label>
            <Input value={session} onChange={(e) => setSession(e.target.value)} placeholder="e.g. Morning Practice" data-testid="attendance-session" />
          </div>
        </div>
        {athletes.length === 0 ? (
          <div className="p-6 text-center text-text-secondary text-sm">No athletes in the academy yet.</div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5">
            {athletes.map((a) => (
              <div key={a.user_id} className="p-3 flex items-center gap-3" data-testid={`attendance-row-${a.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{a.name}</div>
                  <div className="text-[11px] text-text-secondary truncate">{a.email}</div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => mark(a.user_id, s.key)}
                      className={`px-2 py-1 text-[10px] label-tiny tracking-widest border ${
                        marks[a.user_id] === s.key ? s.color + " border-white/30" : "border-white/10 text-text-secondary hover:!border-white/30"
                      }`}
                      data-testid={`attendance-btn-${a.user_id}-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={save}
          disabled={busy || athletes.length === 0}
          className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50"
          data-testid="attendance-save"
        >
          SAVE ATTENDANCE
        </button>
        {rows.length > 0 && (
          <div className="text-[11px] text-text-secondary">
            {rows.length} record(s) saved for {date}.
          </div>
        )}
      </div>
      <div className="border border-white/10">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// 30-DAY SUMMARY</div>
        {summary.length === 0 && <div className="p-6 text-center text-text-secondary text-sm">No attendance data yet.</div>}
        {summary.map((s) => (
          <div key={s.athlete_id} className="p-3 flex items-center gap-3 border-t border-white/5" data-testid={`attendance-summary-${s.athlete_id}`}>
            <div className="flex-1 min-w-0 text-sm">{s.name}</div>
            <div className="text-[10px] text-text-secondary">P:{s.present} · L:{s.late} · A:{s.absent}</div>
            <div className={`text-sm font-heading tabular-nums w-14 text-right ${s.attendance_pct >= 80 ? "text-green-500" : s.attendance_pct >= 50 ? "text-yellow-500" : "text-red-500"}`}>
              {s.attendance_pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EVENT_TYPES = [
  { id: "competition", label: "Competition" },
  { id: "camp",        label: "Camp" },
  { id: "seminar",     label: "Seminar" },
  { id: "practice",    label: "Practice" },
  { id: "match",       label: "Match" },
  { id: "trial",       label: "Trial" },
  { id: "other",       label: "Other" },
];

const EventsTab = ({ academyId, isAdmin }) => {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", event_type: "competition",
    starts_at: "", ends_at: "", location: "", capacity: "",
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/academies/${academyId}/events`);
      setEvents(data.items || []);
    } finally { setBusy(false); }
  }, [academyId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post(`/academies/${academyId}/events`, {
        ...form,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : "",
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : "",
      });
      toast.success("Event created");
      setForm({ title: "", description: "", event_type: "competition", starts_at: "", ends_at: "", location: "", capacity: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try { await api.delete(`/academies/${academyId}/events/${e.event_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const rsvp = async (e, status) => {
    try {
      await api.post(`/academies/${academyId}/events/${e.event_id}/rsvp`, { status });
      toast.success(`RSVP: ${status.toUpperCase()}`);
      load();
    } catch { toast.error("RSVP failed"); }
  };

  return (
    <div className="space-y-4" data-testid="academy-events">
      {isAdmin && (
        <div className="border border-white/10 p-4 space-y-2">
          <div className="label-tiny text-[10px] text-brand-accent mb-2">// CREATE EVENT</div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
            <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="event-type">
              {EVENT_TYPES.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-starts" />
            <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="event-ends" />
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location" />
            <Input placeholder="Capacity (optional)" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="event-capacity" />
          </div>
          <Textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-desc" />
          <button onClick={create} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="event-create">CREATE EVENT</button>
        </div>
      )}
      {busy && <div className="label-tiny text-text-secondary">Loading events...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {events.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No events yet.</div>}
        {events.map((e) => (
          <div key={e.event_id} className="p-4 space-y-2" data-testid={`event-row-${e.event_id}`}>
            <div className="flex items-start gap-3">
              <div className="label-tiny text-[10px] tracking-widest px-2 py-1 bg-brand-accent/15 text-brand-accent shrink-0">
                {e.event_type.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg">{e.title}</div>
                <div className="text-[11px] text-text-secondary">
                  {e.starts_at ? new Date(e.starts_at).toLocaleString() : "TBD"}
                  {e.ends_at && ` → ${new Date(e.ends_at).toLocaleString()}`}
                  {e.location && ` · ${e.location}`}
                  {e.capacity && ` · cap ${e.capacity}`}
                </div>
                {e.description && <div className="text-sm text-text-secondary mt-1">{e.description}</div>}
              </div>
              {isAdmin && (
                <button onClick={() => remove(e)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`event-delete-${e.event_id}`}><Trash size={14} /></button>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <div className="text-[10px] text-text-secondary">
                YES {e.rsvp_counts?.yes || 0} · MAYBE {e.rsvp_counts?.maybe || 0} · NO {e.rsvp_counts?.no || 0}
              </div>
              <div className="flex-1" />
              {["yes", "maybe", "no"].map((s) => (
                <button
                  key={s}
                  onClick={() => rsvp(e, s)}
                  className={`px-2 py-1 text-[10px] label-tiny tracking-widest border ${
                    e.my_rsvp === s ? "border-heat text-heat bg-heat/10" : "border-white/10 text-text-secondary hover:!border-white/30"
                  }`}
                  data-testid={`event-rsvp-${e.event_id}-${s}`}
                >
                  {s === "yes" ? <ThumbsUp size={12} className="inline mr-1" /> : s === "no" ? <ThumbsDown size={12} className="inline mr-1" /> : <Question size={12} className="inline mr-1" />}
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FeesTab = ({ academyId, athletes }) => {
  const [fees, setFees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    athlete_id: "", amount_inr: "", cycle: "monthly", due_date: "", notes: "",
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/academies/${academyId}/fees`);
      setFees(data.items || []);
    } finally { setBusy(false); }
  }, [academyId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.athlete_id || !form.amount_inr || !form.due_date) { toast.error("All fields required"); return; }
    try {
      await api.post(`/academies/${academyId}/fees`, { ...form, amount_inr: parseFloat(form.amount_inr) });
      toast.success("Fee added");
      setForm({ athlete_id: "", amount_inr: "", cycle: "monthly", due_date: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const markPaid = async (f) => {
    try { await api.patch(`/academies/${academyId}/fees/${f.fee_id}`, { status: "paid" }); toast.success("Marked paid"); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete fee for ${f.athlete_name}?`)) return;
    try { await api.delete(`/academies/${academyId}/fees/${f.fee_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const genLink = async (f) => {
    try {
      const { data } = await api.post(`/academies/${academyId}/fees/${f.fee_id}/payment-link`);
      navigator.clipboard.writeText(data.url);
      toast.success("Payment link copied to clipboard");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate link"); }
  };

  const statusColor = (s) => ({
    paid: "bg-green-500/15 text-green-500",
    overdue: "bg-red-500/15 text-red-500",
    due: "bg-yellow-500/15 text-yellow-500",
    waived: "bg-white/10 text-text-secondary",
  }[s] || "bg-white/10 text-text-secondary");

  return (
    <div className="space-y-4" data-testid="academy-fees">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-brand-accent mb-2">// ADD FEE</div>
        <div className="grid md:grid-cols-2 gap-2">
          <select value={form.athlete_id} onChange={(e) => setForm({ ...form, athlete_id: e.target.value })} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="fee-athlete">
            <option value="">Select athlete...</option>
            {athletes.map((a) => (<option key={a.user_id} value={a.user_id}>{a.name}</option>))}
          </select>
          <Input placeholder="Amount (INR)" type="number" value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: e.target.value })} data-testid="fee-amount" />
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <select value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="fee-cycle">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
            <option value="one_time">One-time</option>
          </select>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="fee-due" />
        </div>
        <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="fee-notes" />
        <button onClick={create} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="fee-create">ADD FEE</button>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading fees...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {fees.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No fees recorded yet.</div>}
        {fees.map((f) => (
          <div key={f.fee_id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`fee-row-${f.fee_id}`}>
            <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${statusColor(f.status)}`}>{f.status.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm">{f.athlete_name || f.athlete_id}</div>
              <div className="text-[11px] text-text-secondary">
                ₹{f.amount_inr.toFixed(2)} · {f.cycle} · due {f.due_date}
                {f.notes && ` · ${f.notes}`}
              </div>
              {f.payment_link_url && (
                <div className="text-[10px] text-brand-accent mt-0.5 flex items-center gap-1 truncate">
                  <LinkIcon size={10} /> {f.payment_link_url}
                </div>
              )}
            </div>
            {f.status !== "paid" && (
              <>
                <button onClick={() => genLink(f)} className="px-2 py-1 text-[10px] label-tiny tracking-widest border border-brand-accent/40 text-brand-accent hover:bg-brand-accent/10" data-testid={`fee-link-${f.fee_id}`}>
                  {f.payment_link_url ? "COPY LINK" : "PAYMENT LINK"}
                </button>
                <button onClick={() => markPaid(f)} className="px-2 py-1 text-[10px] label-tiny tracking-widest border border-green-500/40 text-green-500 hover:bg-green-500/10" data-testid={`fee-paid-${f.fee_id}`}>
                  MARK PAID
                </button>
              </>
            )}
            <button onClick={() => remove(f)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`fee-delete-${f.fee_id}`}><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnnouncementsTab = ({ academyId, isAdmin }) => {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/academies/${academyId}/announcements`);
      setRows(data.items || []);
    } finally { setBusy(false); }
  }, [academyId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!msg.trim()) { toast.error("Message required"); return; }
    try {
      await api.post(`/academies/${academyId}/announcements`, { message: msg, cta_text: ctaText, cta_link: ctaLink });
      toast.success("Announcement posted");
      setMsg(""); setCtaText(""); setCtaLink("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
  };

  const toggle = async (a) => {
    try { await api.patch(`/academies/${academyId}/announcements/${a.announcement_id}`, { is_active: !a.is_active }); load(); }
    catch { toast.error("Toggle failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/academies/${academyId}/announcements/${a.announcement_id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="academy-announcements">
      {isAdmin && (
        <div className="border border-white/10 p-4 space-y-2">
          <div className="label-tiny text-[10px] text-brand-accent mb-2">// POST TO ACADEMY</div>
          <Input placeholder="Message (280 chars)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="acad-ann-msg" />
          <div className="flex gap-2 flex-wrap">
            <Input placeholder="CTA text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} className="max-w-[200px]" data-testid="acad-ann-cta-text" />
            <Input placeholder="CTA link" value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} className="flex-1 min-w-[200px]" data-testid="acad-ann-cta-link" />
            <button onClick={create} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="acad-ann-post">POST</button>
          </div>
        </div>
      )}
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No announcements yet.</div>}
        {rows.map((a) => (
          <div key={a.announcement_id} className="p-4 flex items-start gap-3" data-testid={`acad-ann-${a.announcement_id}`}>
            <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${a.is_active ? "bg-green-500/15 text-green-600" : "bg-white/5 text-text-secondary"}`}>
              {a.is_active ? "LIVE" : "OFF"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm">{a.message}</div>
              {a.cta_text && <div className="text-[11px] text-brand-accent mt-0.5">CTA: {a.cta_text} → {a.cta_link}</div>}
              <div className="text-[10px] text-text-secondary/70 mt-0.5">{new Date(a.updated_at).toLocaleString()}</div>
            </div>
            {isAdmin && (
              <>
                <button onClick={() => toggle(a)} className="px-3 py-1 text-[11px] label-tiny tracking-widest border border-white/20 hover:!border-brand-accent" data-testid={`acad-ann-toggle-${a.announcement_id}`}>
                  {a.is_active ? "OFF" : "ON"}
                </button>
                <button onClick={() => remove(a)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`acad-ann-delete-${a.announcement_id}`}><Trash size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const BulkInvitePanel = ({ academyId, onCreated }) => {
  const [emails, setEmails] = useState("");
  const [count, setCount] = useState(10);
  const [role, setRole] = useState("athlete");
  const [days, setDays] = useState(14);
  const [result, setResult] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const emailList = emails.split(/[,;\n\s]+/).map((e) => e.trim()).filter(Boolean);
      const { data } = await api.post(`/academies/${academyId}/invites/bulk`, {
        emails: emailList,
        count: emailList.length ? 0 : parseInt(count, 10) || 10,
        role,
        expires_days: parseInt(days, 10) || 14,
      });
      setResult(data.invites || []);
      toast.success(`Created ${data.count} invite(s)`);
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk invite failed"); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    const lines = result.map((r) => {
      const link = `${window.location.origin}/academy-join/${r.code}`;
      return r.invited_email ? `${r.invited_email}\t${link}` : link;
    }).join("\n");
    navigator.clipboard.writeText(lines);
    toast.success(`Copied ${result.length} invite link(s)`);
  };

  return (
    <div className="border border-white/10 p-4 space-y-2" data-testid="academy-bulk-invite">
      <div className="label-tiny text-[10px] text-brand-accent mb-1">// BULK INVITE (codes only)</div>
      <Textarea
        placeholder={"Paste emails (comma, semicolon, or newline separated) — OR leave empty and set a count below."}
        rows={3}
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
        data-testid="bulk-emails"
      />
      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <label className="label-tiny text-[10px] text-text-secondary">OR RAW COUNT</label>
          <Input type="number" min="1" max="200" value={count} onChange={(e) => setCount(e.target.value)} data-testid="bulk-count" />
        </div>
        <div>
          <label className="label-tiny text-[10px] text-text-secondary">ROLE</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="bulk-role">
            <option value="athlete">Athlete</option>
            <option value="strength_coach">Strength Coach</option>
            <option value="fitness_trainer">Fitness Trainer</option>
            <option value="sports_coach">Sports Coach</option>
            <option value="general_client">General Client</option>
            <option value="parent">Parent</option>
          </select>
        </div>
        <div>
          <label className="label-tiny text-[10px] text-text-secondary">EXPIRES (DAYS)</label>
          <Input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)} data-testid="bulk-days" />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={run} disabled={busy} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="bulk-generate">
          GENERATE INVITES
        </button>
        {result.length > 0 && (
          <button onClick={copyAll} className="px-4 py-2 text-xs label-tiny tracking-widest border border-brand-accent text-brand-accent hover:bg-brand-accent/10" data-testid="bulk-copy-all">
            COPY ALL ({result.length})
          </button>
        )}
      </div>
      {result.length > 0 && (
        <div className="border border-white/10 divide-y divide-white/5 mt-2 max-h-64 overflow-auto" data-testid="bulk-result-list">
          {result.map((r) => (
            <div key={r.code} className="p-2 flex items-center gap-2 text-[11px]" data-testid={`bulk-result-${r.code}`}>
              <span className="font-mono text-brand-accent">{r.code}</span>
              {r.invited_email && <span className="text-text-secondary">→ {r.invited_email}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
